import { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Card, CardActionArea, Divider, Grid, Tooltip, Typography } from "@mui/material"
import { faLocationDot, faMagnifyingGlass, faStore } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { EmptyContent } from "../../../../skeleton/EmptyContent"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { FilterSearch } from "../../FilterSearch"

import type { TakeawayKitchen } from "../takeawayTypes"
import { useApiImageUrl } from "../useApiImageUrl"

const SEARCH_MIN_KITCHENS = 10
const DEFAULT_GRID_BREAKPOINTS = { xs: 12, sm: 6, md: 4 } as const

function matchesSearch(kitchen: TakeawayKitchen, query: string) {
    const fields = [kitchen.name, kitchen.brandingDetails?.description, kitchen.webshop?.name]
    return fields.some((f) => f?.toLowerCase().includes(query))
}

function getGridBreakpoints(count: number) {
    if (count <= 1) return { xs: 12 } as const
    if (count === 2) return { xs: 12, sm: 6 } as const
    return DEFAULT_GRID_BREAKPOINTS
}

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 3rem 3rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        minHeight: "16.5rem",
        justifyContent: "center",
    },
    toolbar: {
        display: "flex",
        justifyContent: "flex-end",
    },
    kitchenCard: {
        height: "100%",
        borderRadius: "1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
    },
    kitchenActionArea: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
    },
    kitchenMedia: {
        width: "100%",
        height: "11rem",
    },
    kitchenImage: {
        objectFit: "cover",
        display: "block",
    },
    kitchenImagePlaceholder: {
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    kitchenContent: {
        display: "flex",
        flexDirection: "column",
    },
    kitchenName: {
        padding: ".375rem .75rem 0",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[700],
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    kitchenDescription: {
        padding: "0 .75rem .375rem",
        color: theme.palette.tailwind.slate[500],
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    kitchenMerchant: {
        padding: ".5rem .75rem",
        color: theme.palette.tailwind.slate[500],
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        lineHeight: 1.6,
    },
    kitchenMerchantIcon: {
        fontSize: ".875rem",
    },
    kitchenMerchantText: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    kitchenDivider: {
        borderStyle: "dashed",
        borderColor: theme.palette.tailwind.slate[200],
    },
}))

type KitchenCardProps = Readonly<{
    kitchen: TakeawayKitchen
    onSelect: (kitchen: TakeawayKitchen) => void
}>

function KitchenCard({ kitchen, onSelect }: KitchenCardProps) {
    const { classes, cx } = useStyles()
    const resolvedImageUrl = useApiImageUrl(kitchen.brandingDetails?.imageUrl)
    const shopTitle = kitchen.webshop?.name
    const description = kitchen.brandingDetails?.description
    const merchantName = kitchen.name

    return (
        <Card className={classes.kitchenCard} elevation={0}>
            <CardActionArea className={classes.kitchenActionArea} onClick={() => onSelect(kitchen)}>
                {resolvedImageUrl ? (
                    <img
                        src={resolvedImageUrl}
                        className={cx(classes.kitchenMedia, classes.kitchenImage)}
                        alt={shopTitle ?? merchantName}
                    />
                ) : (
                    <Box className={cx(classes.kitchenMedia, classes.kitchenImagePlaceholder)} />
                )}
                <Box className={classes.kitchenContent}>
                    {shopTitle && (
                        <Tooltip title={shopTitle} arrow>
                            <Typography variant="subtitle1" className={classes.kitchenName}>
                                {shopTitle}
                            </Typography>
                        </Tooltip>
                    )}
                    {description && (
                        <Tooltip title={description} arrow>
                            <Typography variant="body2" className={classes.kitchenDescription}>
                                {description}
                            </Typography>
                        </Tooltip>
                    )}
                    {merchantName && (
                        <>
                            <Divider className={classes.kitchenDivider} />
                            <Box className={classes.kitchenMerchant}>
                                <FontAwesomeIcon icon={faLocationDot} className={classes.kitchenMerchantIcon} />
                                <Tooltip title={merchantName} arrow>
                                    <Typography variant="body2" className={classes.kitchenMerchantText}>
                                        {merchantName}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                </Box>
            </CardActionArea>
        </Card>
    )
}

function KitchenSelectorEmpty() {
    return (
        <Box>
            <LocalizedStrict id="takeaway-no-shops-empty" attrs={{ title: true }}>
                <EmptyContent
                    icon={faStore}
                    backgroundColor={tailwindPalette.slate[400]}
                    title="No takeaway shops available"
                    description=""
                />
            </LocalizedStrict>
        </Box>
    )
}

function KitchenSelectorNoResults() {
    return (
        <Box>
            <LocalizedStrict id="takeaway-menu-no-search-results-empty" attrs={{ title: true }}>
                <EmptyContent
                    icon={faMagnifyingGlass}
                    backgroundColor={tailwindPalette.slate[400]}
                    title="No results"
                    description=""
                />
            </LocalizedStrict>
        </Box>
    )
}

type TakeawayKitchenSelectorDialogProps = Readonly<{
    kitchens: TakeawayKitchen[]
    onSelect: (kitchen: TakeawayKitchen) => void
    onClose: () => void
}>

export function TakeawayKitchenSelectorDialog({ kitchens, onSelect, onClose }: TakeawayKitchenSelectorDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [search, setSearch] = useState("")

    const filteredKitchens = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return kitchens
        return kitchens.filter((k) => matchesSearch(k, query))
    }, [kitchens, search])

    const showSearch = kitchens.length >= SEARCH_MIN_KITCHENS

    function renderBody() {
        if (!kitchens.length) return <KitchenSelectorEmpty />
        if (!filteredKitchens.length) return <KitchenSelectorNoResults />
        const gridBreakpoints = getGridBreakpoints(filteredKitchens.length)
        return (
            <Grid container spacing={3}>
                {filteredKitchens.map((kitchen, index) => (
                    <Grid item {...gridBreakpoints} key={`${kitchen.id}-${index}`}>
                        <KitchenCard kitchen={kitchen} onSelect={onSelect} />
                    </Grid>
                ))}
            </Grid>
        )
    }

    return (
        <StandardDialog
            open
            onClose={onClose}
            dialogTitle={l10n.getString("takeaway-kitchen-selector-title")}
            maxWidthProp="md"
        >
            <Box className={classes.content}>
                {showSearch && (
                    <Box className={classes.toolbar}>
                        <FilterSearch search={search} setSearch={setSearch} defaultActive activeWidth="18rem" />
                    </Box>
                )}
                {renderBody()}
            </Box>
        </StandardDialog>
    )
}
